import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  User, Mail, Lock, QrCode, Smartphone, Share2, MessageCircle,
  Building2, Phone, Eye, EyeOff, ArrowLeft
} from "lucide-react";
import axios from "axios";
import WhatsAppButton from "@/components/WhatsAppButton";

const PasswordInput = ({ id, testid, placeholder, value, onChange }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        id={id}
        data-testid={testid}
        type={show ? "text" : "password"}
        placeholder={placeholder}
        className="pl-10 pr-10 h-12"
        value={value}
        onChange={onChange}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        data-testid={`${testid}-toggle`}
        tabIndex={-1}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, login, setSession } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("register");

  const [accountType, setAccountType] = useState("individual"); // individual | company
  const [registerStep, setRegisterStep] = useState("form"); // form | otp
  const [otp, setOtp] = useState("");
  const [resendIn, setResendIn] = useState(0);

  const [indivForm, setIndivForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [companyForm, setCompanyForm] = useState({ company_name: "", name: "", email: "", phone: "", password: "", confirm: "" });
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [showLoginPw, setShowLoginPw] = useState(false);

  const currentForm = accountType === "individual" ? indivForm : companyForm;
  const setCurrentForm = accountType === "individual" ? setIndivForm : setCompanyForm;

  useEffect(() => {
    if (user) {
      if (user.role === "superadmin") navigate("/superadmin");
      else if (user.role === "company_admin") navigate("/company-admin");
      else navigate("/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/landing-settings`);
        setSettings(response.data);
      } catch (error) {
        // Settings are optional here — fall back to defaults
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const validateForm = () => {
    const f = currentForm;
    if (accountType === "company" && (!f.company_name || !f.phone)) {
      toast.error("Please fill in all company details");
      return false;
    }
    if (!f.name || !f.email || !f.password) {
      toast.error("Please fill in all fields");
      return false;
    }
    if (f.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return false;
    }
    if (f.password !== f.confirm) {
      toast.error("Passwords do not match");
      return false;
    }
    return true;
  };

  const sendOtp = async () => {
    await axios.post(`${API}/auth/send-otp`, { email: currentForm.email, account_type: accountType });
    setResendIn(30);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      await sendOtp();
      toast.success("Verification code sent to your email");
      setOtp("");
      setRegisterStep("otp");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not send verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0) return;
    try {
      await sendOtp();
      toast.success("A new code has been sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not resend code");
    }
  };

  const handleVerifyRegister = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit code from your email");
      return;
    }
    setLoading(true);
    try {
      let resp;
      if (accountType === "individual") {
        resp = await axios.post(`${API}/auth/register/individual`, {
          name: indivForm.name, email: indivForm.email, password: indivForm.password, otp,
        });
      } else {
        resp = await axios.post(`${API}/auth/register/company`, {
          company_name: companyForm.company_name, name: companyForm.name,
          email: companyForm.email, phone: companyForm.phone, password: companyForm.password, otp,
        });
      }
      setSession(resp.data.access_token, resp.data.user);
      toast.success("Welcome aboard!");
      navigate(resp.data.user.role === "company_admin" ? "/company-admin" : "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      const userData = await login(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      if (userData.role === "company_admin") navigate("/company-admin");
      else navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 brand-gradient opacity-5"></div>
        <div className="container mx-auto px-6 py-8">
          <motion.header
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between mb-12 relative z-10"
          >
            <div className="flex items-center gap-3">
              {settings?.logo_url ? (
                <img src={settings.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}` : settings.logo_url} alt="Logo" className="h-10 w-auto" />
              ) : (
                <div className="h-10 w-10 rounded-xl brand-gradient flex items-center justify-center">
                  <QrCode className="h-6 w-6 text-white" />
                </div>
              )}
              <span className="font-bold text-xl">{settings?.logo_text || "tap to share"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate("/support")} data-testid="header-support-btn" className="gap-2 cursor-pointer">
                <MessageCircle className="h-4 w-4" />
                <span className="hidden sm:inline">Support</span>
              </Button>
              <Button variant="outline" size="icon" onClick={() => navigate("/superadmin-login")} data-testid="header-admin-btn" className="cursor-pointer">
                <Lock className="h-4 w-4" />
              </Button>
            </div>
          </motion.header>

          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-200px)]">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="space-y-6">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                {settings?.welcome_title || "Your Digital Business Card"}
              </h1>
              <p className="text-lg text-muted-foreground max-w-lg">
                {settings?.welcome_subtitle || "Create and share your professional profile with NFC technology. One tap to connect."}
              </p>
              <div className="grid sm:grid-cols-3 gap-4 pt-6">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="flex items-center gap-3 p-4 rounded-xl bg-card border">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"><QrCode className="h-5 w-5 text-primary" /></div>
                  <span className="font-medium text-sm">QR Code</span>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="flex items-center gap-3 p-4 rounded-xl bg-card border">
                  <div className="h-10 w-10 rounded-lg bg-secondary/10 flex items-center justify-center"><Smartphone className="h-5 w-5 text-secondary" /></div>
                  <span className="font-medium text-sm">NFC Ready</span>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="flex items-center gap-3 p-4 rounded-xl bg-card border">
                  <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center"><Share2 className="h-5 w-5 text-accent" /></div>
                  <span className="font-medium text-sm">Easy Share</span>
                </motion.div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <Card className="w-full max-w-md mx-auto shadow-xl border-0 bg-card/80 backdrop-blur">
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-2xl">Get Started</CardTitle>
                  <CardDescription>Create your digital business card in seconds</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGoogle}
                    className="w-full h-12 rounded-full gap-2 mb-4"
                    data-testid="google-signin-btn"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                    </svg>
                    Continue with Google
                  </Button>
                  <div className="relative mb-4">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">or continue with email</span>
                    </div>
                  </div>
                  <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setRegisterStep("form"); }}>
                    <TabsList className="grid w-full grid-cols-2 mb-6 h-12">
                      <TabsTrigger value="register" data-testid="register-tab" className="h-10 text-base">Register</TabsTrigger>
                      <TabsTrigger value="login" data-testid="login-tab" className="h-10 text-base">Login</TabsTrigger>
                    </TabsList>

                    <TabsContent value="register">
                      {registerStep === "form" ? (
                        <>
                          {/* Account type toggle */}
                          <div className="grid grid-cols-2 gap-2 mb-5">
                            <button
                              type="button"
                              onClick={() => setAccountType("individual")}
                              data-testid="account-type-individual"
                              className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-colors ${accountType === "individual" ? "border-primary bg-primary/5" : "border-border"}`}
                            >
                              <User className="h-5 w-5" />
                              <span className="text-sm font-medium">Individual</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setAccountType("company")}
                              data-testid="account-type-company"
                              className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-colors ${accountType === "company" ? "border-primary bg-primary/5" : "border-border"}`}
                            >
                              <Building2 className="h-5 w-5" />
                              <span className="text-sm font-medium">Company</span>
                            </button>
                          </div>

                          <form onSubmit={handleSendOtp} className="space-y-4">
                            {accountType === "company" && (
                              <div className="space-y-2">
                                <Label htmlFor="company-name">Company Name</Label>
                                <div className="relative">
                                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                  <Input id="company-name" data-testid="company-name-input" placeholder="Acme Inc." className="pl-10 h-12"
                                    value={companyForm.company_name} onChange={(e) => setCompanyForm({ ...companyForm, company_name: e.target.value })} />
                                </div>
                              </div>
                            )}

                            <div className="space-y-2">
                              <Label htmlFor="reg-name">Full Name</Label>
                              <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input id="reg-name" data-testid="register-name-input" placeholder="John Doe" className="pl-10 h-12"
                                  value={currentForm.name} onChange={(e) => setCurrentForm({ ...currentForm, name: e.target.value })} />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="reg-email">{accountType === "company" ? "Work Email" : "Email"}</Label>
                              <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input id="reg-email" data-testid="register-email-input" type="email"
                                  placeholder={accountType === "company" ? "you@yourcompany.com" : "john@example.com"} className="pl-10 h-12"
                                  value={currentForm.email} onChange={(e) => setCurrentForm({ ...currentForm, email: e.target.value })} />
                              </div>
                              {accountType === "company" && (
                                <p className="text-xs text-muted-foreground">Use your company work email (personal email domains are not allowed).</p>
                              )}
                            </div>

                            {accountType === "company" && (
                              <div className="space-y-2">
                                <Label htmlFor="company-phone">Phone</Label>
                                <div className="relative">
                                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                  <Input id="company-phone" data-testid="company-phone-input" placeholder="+1 234 567 8900" className="pl-10 h-12"
                                    value={companyForm.phone} onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })} />
                                </div>
                              </div>
                            )}

                            <div className="space-y-2">
                              <Label htmlFor="reg-password">Password</Label>
                              <PasswordInput id="reg-password" testid="register-password-input" placeholder="Create a password"
                                value={currentForm.password} onChange={(e) => setCurrentForm({ ...currentForm, password: e.target.value })} />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="reg-confirm">Confirm Password</Label>
                              <PasswordInput id="reg-confirm" testid="register-confirm-input" placeholder="Re-enter password"
                                value={currentForm.confirm} onChange={(e) => setCurrentForm({ ...currentForm, confirm: e.target.value })} />
                            </div>

                            <Button type="submit" className="w-full h-12 rounded-full" disabled={loading} data-testid="register-submit-btn">
                              {loading ? "Sending code..." : "Continue"}
                            </Button>
                          </form>
                        </>
                      ) : (
                        <form onSubmit={handleVerifyRegister} className="space-y-5">
                          <button type="button" onClick={() => setRegisterStep("form")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="otp-back-btn">
                            <ArrowLeft className="h-4 w-4" /> Back
                          </button>
                          <div className="text-center space-y-1">
                            <Mail className="h-8 w-8 mx-auto text-primary" />
                            <p className="text-sm text-muted-foreground">
                              We sent a 6-digit code to<br /><span className="font-medium text-foreground">{currentForm.email}</span>
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="otp-input">Verification Code</Label>
                            <Input
                              id="otp-input"
                              data-testid="otp-input"
                              inputMode="numeric"
                              maxLength={6}
                              placeholder="------"
                              className="h-14 text-center text-2xl tracking-[0.5em] font-semibold"
                              value={otp}
                              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            />
                          </div>
                          <Button type="submit" className="w-full h-12 rounded-full" disabled={loading} data-testid="otp-verify-btn">
                            {loading ? "Verifying..." : "Verify & Create Account"}
                          </Button>
                          <div className="text-center text-sm text-muted-foreground">
                            Didn&apos;t get it?{" "}
                            <button type="button" onClick={handleResend} disabled={resendIn > 0} className="text-primary hover:underline disabled:opacity-50 disabled:no-underline" data-testid="otp-resend-btn">
                              {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                            </button>
                          </div>
                        </form>
                      )}
                    </TabsContent>

                    <TabsContent value="login">
                      <form onSubmit={handleLogin} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="login-email">Email</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input id="login-email" data-testid="login-email-input" type="email" placeholder="john@example.com" className="pl-10 h-12"
                              value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label htmlFor="login-password">Password</Label>
                            <button type="button" onClick={() => navigate("/forgot-password")} className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                              Forgot password?
                            </button>
                          </div>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input id="login-password" data-testid="login-password-input" type={showLoginPw ? "text" : "password"} placeholder="Your password" className="pl-10 pr-10 h-12"
                              value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
                            <button type="button" onClick={() => setShowLoginPw(!showLoginPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="login-password-toggle" tabIndex={-1}>
                              {showLoginPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        <Button type="submit" className="w-full h-12 rounded-full" disabled={loading} data-testid="login-submit-btn">
                          {loading ? "Signing in..." : "Sign In"}
                        </Button>
                      </form>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>

      <footer className="border-t py-6 mt-auto">
        <div className="container mx-auto px-6 text-center text-sm text-muted-foreground">
          {settings?.footer_text || "© 2026 h75c. All rights reserved."}
        </div>
      </footer>

      <WhatsAppButton phone={settings?.support_whatsapp} />
    </div>
  );
};

export default LandingPage;
